import React from 'react';
import { 
  X, 
  Check, 
  Crown, 
  Zap, 
  ArrowRight, 
  Lock, 
  Gift, 
  ShieldCheck,
  Clock
} from 'lucide-react';
import { useIsPlayStore } from '../../../utils/playstore';

export type UserPlanTier = 'free' | 'pro' | 'premium' | 'enterprise';

interface PlanUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  requiredTier: 'pro' | 'premium' | 'enterprise';
  currentTier?: UserPlanTier;
  featureName: string;
  featureDescription?: string;
  onSelectPlan: (tier: UserPlanTier) => void;
  trialUsed?: boolean;
  onStartTrial?: () => void;
  isActivatingTrial?: boolean;
}

export default function PlanUpgradeModal({
  isOpen,
  onClose,
  requiredTier,
  currentTier = 'free',
  featureName,
  featureDescription,
  onSelectPlan,
  trialUsed = false,
  onStartTrial,
  isActivatingTrial = false
}: PlanUpgradeModalProps) {
  const isPlayStore = useIsPlayStore();

  if (!isOpen) return null;

  const canShowTrial = trialUsed === false && !!onStartTrial;

  return (
    <div className="fixed inset-0 z-100 bg-neutral-950/85 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-2xl bg-white border border-zinc-100 rounded-3xl p-6 sm:p-8 space-y-5 shadow-2xl text-xs max-h-[95vh] overflow-y-auto font-sans relative">
        {/* Botão Fechar */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-5 right-5 p-2 bg-zinc-100 hover:bg-zinc-200 text-zinc-600 rounded-full transition-colors cursor-pointer active:scale-95"
        >
          <X className="h-4 w-4" />
        </button>

        {isPlayStore ? (
          /* MODO GOOGLE PLAY STORE (COMPLIANT / GESTÃO SAAS) */
          <div className="py-2 px-2 flex flex-col items-center text-center space-y-4">
            <div className="w-12 h-12 rounded-3xl bg-brand/10 border border-brand/20 flex items-center justify-center text-brand">
              <Crown className="h-6 w-6" />
            </div>

            <div className="space-y-2 max-w-sm">
              <span className="text-[10px] font-black text-brand uppercase tracking-widest bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-100">
                {featureName}
              </span>
              <h2 className="text-lg sm:text-xl font-black text-zinc-900 leading-tight">
                {requiredTier === 'premium' ? 'Recurso do Plano Especialista' : 'Recurso do Plano PRO'}
              </h2>
              <p className="text-zinc-600 text-xs leading-relaxed">
                {featureDescription || 'Tenha mais controle, organização e ferramentas completas para crescer seu negócio com segurança e lucro.'}
              </p>
            </div>

            {/* Se elegível ao Test Drive de 14 Dias Grátis (100% Gratuito / Permitido no Google Play) */}
            {canShowTrial && (
              <div className="w-full max-w-md p-4 bg-linear-to-br from-emerald-950 via-zinc-900 to-zinc-900 border-2 border-emerald-500 rounded-2xl text-white shadow-lg space-y-2.5 text-left">
                <div className="flex items-start gap-2.5">
                  <div className="p-2 bg-emerald-500/20 text-emerald-400 rounded-xl border border-emerald-500/30 shrink-0 mt-0.5">
                    <Zap className="h-5 w-5" />
                  </div>
                  <div className="space-y-0.5">
                    <h3 className="text-xs sm:text-sm font-black text-white leading-tight">
                      Experimente 14 Dias Grátis
                    </h3>
                    <p className="text-zinc-300 text-[11px] leading-relaxed">
                      Ative seu <strong>Test Drive sem cartão</strong> com acesso total ao plano <strong>Especialista Premium</strong>.
                    </p>
                  </div>
                </div>

                <div className="pt-1 flex flex-col sm:flex-row items-center gap-2.5">
                  <button
                    type="button"
                    disabled={isActivatingTrial}
                    onClick={onStartTrial}
                    className="w-full sm:w-auto flex-1 py-3 px-4 bg-emerald-500 hover:bg-emerald-400 text-neutral-950 font-black text-xs uppercase tracking-wider rounded-xl shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-95 disabled:opacity-50"
                  >
                    <Zap className="h-4 w-4" />
                    <span>{isActivatingTrial ? 'Ativando...' : 'Ativar 14 Dias Grátis'}</span>
                    <ArrowRight className="h-4 w-4" />
                  </button>

                  <div className="flex items-center gap-1 text-[10px] text-emerald-300 font-medium shrink-0">
                    <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />
                    <span>Sem cartão</span>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-1 max-w-sm">
              <h2 className="text-xl font-black text-zinc-900 leading-tight">
                Disponível na Web
              </h2>
              <p className="text-zinc-500 text-xs">
                As assinaturas definitivas são gerenciadas através do nosso site oficial. Acesse pelo navegador de internet do seu celular ou computador para gerenciar seu plano.
              </p>
            </div>

            <button 
              type="button"
              onClick={onClose}
              className="w-full max-w-xs py-3 bg-neutral-900 hover:bg-neutral-800 text-white font-black rounded-2xl transition-all uppercase tracking-wider text-xs cursor-pointer active:scale-95"
            >
              Fechar
            </button>
          </div>
        ) : (
          /* MODO WEB COM TESTE DE 14 DIAS OU CHECKOUT DIRETO */
          <>
            {/* Cabeçalho de Destaque */}
            <div className="text-center space-y-2 max-w-lg mx-auto pt-1">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-linear-to-r from-amber-500/10 to-orange-500/10 border border-amber-300 text-amber-900 font-black text-[10px] uppercase tracking-widest">
                <Lock className="h-3.5 w-3.5 text-amber-600" />
                <span>Acesso ao {requiredTier === 'enterprise' ? 'Plano Empresa' : (requiredTier === 'premium' ? 'Plano Especialista' : 'Plano PRO')}</span>
              </div>

              <h2 className="text-xl sm:text-2xl font-black text-zinc-900 leading-tight">
                Ative este recurso: {featureName}
              </h2>

              <p className="text-zinc-500 font-medium text-xs">
                {featureDescription || 'Tenha mais controle, organização e ferramentas completas para crescer seu negócio com segurança e lucro.'}
              </p>

              {/* Se o trial expirou anteriormente */}
              {trialUsed && currentTier === 'free' && (
                <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-2xl text-left text-[11px] text-amber-900 flex items-center gap-2 mt-2">
                  <Clock className="h-4 w-4 text-amber-600 shrink-0" />
                  <span>
                    Seu período de teste de 14 dias encerrou. Seus orçamentos e cadastros continuam <strong>100% salvos</strong>! Escolha um plano abaixo para reativar todos os recursos.
                  </span>
                </div>
              )}

              {/* Banner de Incentivo para quem já é PRO */}
              {currentTier === 'pro' && (
                <div className="p-3 bg-linear-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl text-left text-[11px] text-amber-950 flex items-start gap-2.5 shadow-xs mt-2">
                  <Zap className="h-4 w-4 text-orange-600 shrink-0 mt-0.5" />
                  <div>
                    <strong className="block font-black text-amber-900">Vantagem para quem já é PRO:</strong>
                    <span>Ao migrar para o Especialista ou Plano Empresa hoje, você usa tudo imediatamente e o novo valor <strong>só será cobrado no próximo mês</strong>!</span>
                  </div>
                </div>
              )}
            </div>

            {/* SEÇÃO PRINCIPAL: TESTAR 14 DIAS GRÁTIS (SE ELEGÍVEL) */}
            {canShowTrial && (
              <div className="p-4 sm:p-5 bg-linear-to-br from-emerald-950 via-zinc-900 to-zinc-900 border-2 border-emerald-500 rounded-3xl text-white shadow-xl space-y-3 relative overflow-hidden">
                <div className="absolute top-0 right-0 transform translate-x-3 -translate-y-3 bg-emerald-500 text-neutral-950 font-black text-[9px] uppercase tracking-widest px-8 py-1 rotate-12 shadow-sm">
                  14 Dias Grátis
                </div>

                <div className="flex items-start gap-3">
                  <div className="p-2.5 bg-emerald-500/20 text-emerald-400 rounded-2xl border border-emerald-500/30 shrink-0 mt-0.5">
                    <Zap className="h-6 w-6" />
                  </div>
                  <div className="space-y-1">
                    <h3 className="text-sm sm:text-base font-black text-white leading-tight">
                      Deseja experimentar antes de pagar?
                    </h3>
                    <p className="text-zinc-300 text-xs leading-relaxed">
                      Ative seu <strong>Test Drive de 14 Dias Grátis</strong> agora mesmo com acesso total aos recursos avançados do R-MAX.
                    </p>
                  </div>
                </div>

                <div className="pt-1 flex flex-col sm:flex-row items-center gap-3">
                  <button
                    type="button"
                    disabled={isActivatingTrial}
                    onClick={onStartTrial}
                    className="w-full sm:w-auto flex-1 py-3.5 px-5 bg-emerald-500 hover:bg-emerald-400 text-neutral-950 font-black text-xs uppercase tracking-wider rounded-2xl shadow-lg shadow-emerald-500/20 transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-95 disabled:opacity-50"
                  >
                    <Zap className="h-4 w-4" />
                    <span>{isActivatingTrial ? 'Ativando seu teste...' : 'Ativar 14 Dias Grátis Agora'}</span>
                    <ArrowRight className="h-4 w-4" />
                  </button>

                  <div className="flex items-center gap-1.5 text-[11px] text-emerald-300 font-medium shrink-0">
                    <ShieldCheck className="h-4 w-4 text-emerald-400" />
                    <span>Sem cartão de crédito</span>
                  </div>
                </div>
              </div>
            )}

            {canShowTrial && (
              <div className="flex items-center gap-3 text-zinc-400 text-[10px] uppercase tracking-widest font-black">
                <div className="h-px bg-zinc-200 flex-1" />
                <span>Ou assine diretamente</span>
                <div className="h-px bg-zinc-200 flex-1" />
              </div>
            )}

            {/* Grade Comparativa de Planos (3 Colunas) */}
            <div className="grid md:grid-cols-3 gap-3.5 pt-1">
              {/* 1. PLANO PRO */}
              <div className={`p-4 rounded-3xl border-2 transition-all flex flex-col justify-between space-y-3 ${
                requiredTier === 'pro'
                  ? 'border-brand bg-emerald-50/20 shadow-md ring-2 ring-brand/20'
                  : 'border-zinc-100 bg-zinc-50/50'
              }`}>
                <div className="space-y-2.5">
                  <div className="flex items-center justify-between">
                    <div className="w-8 h-8 rounded-2xl bg-emerald-50 text-brand flex items-center justify-center font-black border border-emerald-100">
                      <Zap className="h-4 w-4" />
                    </div>
                    <span className="text-[9px] font-black uppercase tracking-widest bg-emerald-50 text-brand px-2 py-0.5 rounded-full border border-emerald-100">
                      Individual
                    </span>
                  </div>

                  <div>
                    <h3 className="text-sm font-black text-zinc-900">Plano PRO</h3>
                    <div className="mt-0.5 flex items-baseline gap-1">
                      <span className="text-xl font-black text-zinc-900">R$ 19,90</span>
                      <span className="text-zinc-500 font-bold">/mês</span>
                    </div>
                  </div>

                  <ul className="space-y-1.5 pt-2 border-t border-zinc-100 text-zinc-700 text-[11px]">
                    <li className="flex items-center gap-1.5 font-bold">
                      <Check className="h-3.5 w-3.5 text-brand shrink-0" />
                      <span>Orçamentos Ilimitados</span>
                    </li>
                    <li className="flex items-center gap-1.5">
                      <Check className="h-3.5 w-3.5 text-brand shrink-0" />
                      <span>Custos, Insumos e Serviços</span>
                    </li>
                    <li className="flex items-center gap-1.5">
                      <Check className="h-3.5 w-3.5 text-brand shrink-0" />
                      <span>Propostas em PDF</span>
                    </li>
                    <li className="flex items-center gap-1.5">
                      <Check className="h-3.5 w-3.5 text-brand shrink-0" />
                      <span>Cálculo do Valor da Hora</span>
                    </li>
                  </ul>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    onSelectPlan('pro');
                    onClose();
                  }}
                  className="w-full py-2.5 px-3 bg-neutral-950 hover:bg-neutral-900 text-white font-black uppercase tracking-wider rounded-xl shadow-xs transition-all flex items-center justify-center gap-1 text-[11px] cursor-pointer active:scale-95"
                >
                  <span>Escolher PRO</span>
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>

              {/* 2. PLANO ESPECIALISTA PREMIUM */}
              <div className={`p-4 rounded-3xl border-2 transition-all flex flex-col justify-between space-y-3 relative ${
                requiredTier === 'premium'
                  ? 'border-orange-500 bg-orange-50/30 shadow-md ring-2 ring-orange-500/20'
                  : 'border-zinc-200 bg-zinc-50/50'
              }`}>
                <div className="space-y-2.5">
                  <div className="flex items-center justify-between">
                    <div className="w-8 h-8 rounded-2xl bg-orange-100 text-orange-800 flex items-center justify-center font-black">
                      <Crown className="h-4 w-4" />
                    </div>
                    <span className="text-[9px] font-black uppercase tracking-widest bg-orange-100 text-orange-800 px-2 py-0.5 rounded-full">
                      Equipes
                    </span>
                  </div>

                  <div>
                    <h3 className="text-sm font-black text-zinc-900">Especialista</h3>
                    <div className="mt-0.5 flex items-baseline gap-1">
                      <span className="text-xl font-black text-zinc-900">R$ 49,90</span>
                      <span className="text-zinc-500 font-bold">/mês</span>
                    </div>
                  </div>

                  <ul className="space-y-1.5 pt-2 border-t border-zinc-200 text-zinc-800 text-[11px]">
                    <li className="flex items-center gap-1.5 font-bold text-orange-950">
                      <Check className="h-3.5 w-3.5 text-orange-600 shrink-0" />
                      <span>Equipe: até 10 colaboradores</span>
                    </li>
                    <li className="flex items-center gap-1.5 font-medium">
                      <Check className="h-3.5 w-3.5 text-orange-600 shrink-0" />
                      <span>Trava de Margem Comercial</span>
                    </li>
                    <li className="flex items-center gap-1.5 font-medium">
                      <Check className="h-3.5 w-3.5 text-orange-600 shrink-0" />
                      <span>OS com Fotos Antes/Depois</span>
                    </li>
                    <li className="flex items-center gap-1.5 font-medium">
                      <Check className="h-3.5 w-3.5 text-orange-600 shrink-0" />
                      <span>Assinatura do Cliente na Tela</span>
                    </li>
                    <li className="flex items-center gap-1.5 font-medium">
                      <Check className="h-3.5 w-3.5 text-orange-600 shrink-0" />
                      <span>Agenda & Link Online</span>
                    </li>
                    <li className="flex items-center gap-1.5 font-medium">
                      <Check className="h-3.5 w-3.5 text-orange-600 shrink-0" />
                      <span>Recibos com QR Code PIX</span>
                    </li>
                  </ul>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    onSelectPlan('premium');
                    onClose();
                  }}
                  className="w-full py-2.5 px-3 bg-linear-to-r from-orange-600 to-amber-600 hover:from-orange-500 hover:to-amber-500 text-white font-black uppercase tracking-wider rounded-xl shadow-xs transition-all flex items-center justify-center gap-1 text-[11px] cursor-pointer active:scale-95"
                >
                  <span>Escolher Especialista</span>
                  <Crown className="h-3.5 w-3.5" />
                </button>
              </div>

              {/* 3. PLANO EMPRESA */}
              <div className={`p-4 rounded-3xl border-2 transition-all flex flex-col justify-between space-y-3 relative ${
                requiredTier === 'enterprise'
                  ? 'border-indigo-600 bg-linear-to-b from-indigo-50/40 to-blue-50/30 shadow-xl ring-2 ring-indigo-500/20'
                  : 'border-indigo-300 bg-indigo-50/20'
              }`}>
                <div className="absolute -top-3 right-4 bg-linear-to-r from-indigo-600 to-blue-600 text-white text-[9px] font-black uppercase tracking-widest px-2.5 py-0.5 rounded-full shadow-xs flex items-center gap-1">
                  <Crown className="h-3 w-3" />
                  <span>Plano Empresa</span>
                </div>

                <div className="space-y-2.5">
                  <div className="flex items-center justify-between">
                    <div className="w-8 h-8 rounded-2xl bg-indigo-100 text-indigo-900 flex items-center justify-center font-black">
                      <ShieldCheck className="h-4 w-4 text-indigo-700" />
                    </div>
                    <span className="text-[9px] font-black uppercase tracking-widest bg-indigo-100 text-indigo-900 px-2 py-0.5 rounded-full">
                      Clínicas & Médicos
                    </span>
                  </div>

                  <div>
                    <h3 className="text-sm font-black text-zinc-900">Plano Empresa</h3>
                    <div className="mt-0.5 flex items-baseline gap-1">
                      <span className="text-xl font-black text-zinc-900">R$ 99,00</span>
                      <span className="text-zinc-500 font-bold">/mês</span>
                    </div>
                  </div>

                  <ul className="space-y-1.5 pt-2 border-t border-indigo-200 text-zinc-900 text-[11px]">
                    <li className="flex items-center gap-1.5 font-bold text-indigo-950">
                      <Check className="h-3.5 w-3.5 text-indigo-600 shrink-0" />
                      <span>Gestão de Clínicas Médicas & Vet</span>
                    </li>
                    <li className="flex items-center gap-1.5 font-bold text-indigo-950">
                      <Check className="h-3.5 w-3.5 text-indigo-600 shrink-0" />
                      <span>Estoque Completo com Lotes & Validade</span>
                    </li>
                    <li className="flex items-center gap-1.5 font-bold text-indigo-950">
                      <Check className="h-3.5 w-3.5 text-indigo-600 shrink-0" />
                      <span>Prontuário SOAP com Valor Jurídico</span>
                    </li>
                    <li className="flex items-center gap-1.5 font-bold text-indigo-950">
                      <Check className="h-3.5 w-3.5 text-indigo-600 shrink-0" />
                      <span>Central de Retorno de Vacinas no WhatsApp</span>
                    </li>
                    <li className="flex items-center gap-1.5 font-bold text-indigo-950">
                      <Check className="h-3.5 w-3.5 text-indigo-600 shrink-0" />
                      <span>Cofre DICOM R2 (Guarda 5 Anos)</span>
                    </li>
                    <li className="flex items-center gap-1.5 text-zinc-600">
                      <Check className="h-3.5 w-3.5 text-indigo-600 shrink-0" />
                      <span>Tudo do Plano Especialista incluso</span>
                    </li>
                  </ul>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    onSelectPlan('enterprise');
                    onClose();
                  }}
                  className="w-full py-2.5 px-3 bg-linear-to-r from-indigo-700 to-blue-700 hover:from-indigo-600 hover:to-blue-600 text-white font-black uppercase tracking-wider rounded-xl shadow-md transition-all flex items-center justify-center gap-1 text-[11px] cursor-pointer active:scale-95"
                >
                  <span>Escolher Plano Empresa</span>
                  <Crown className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>

            {/* Nota de Cupom & Garantia */}
            <div className="p-4 bg-zinc-50 rounded-2xl border border-zinc-100 flex items-center justify-between text-[11px] text-zinc-600">
              <div className="flex items-center gap-2">
                <Gift className="h-4 w-4 text-orange-600 shrink-0" />
                <span>Tem cupom de parceiro? O desconto será aplicado no pagamento.</span>
              </div>
              <span className="text-zinc-400 font-bold hidden sm:inline">Cancele quando quiser</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
